import { ParsedUrlQuery } from 'querystring';
import { BasicSeoType, SeoType } from '@/types/seo';

export const defaultSeo: SeoType = {
  title: 'Bee Maps - Build a Decentralized Global Map',
  description:
    "Bee Maps is building a decentralized global map of the world. Join our network and take part in building a map of the world that's accessible to everyone.",
  keywords:
    'Bee Maps Contribute, Drive to Earn, Map AI Training Games, HONEY Rewards, Community Mapping, Global Map Data, Fleet Mapping Solutions, Drive and Earn, AI-Powered Mapping, Real-Time Map Updates',
  image:
    'https://hivemapper-static.s3.us-west-2.amazonaws.com/website/public/seo/beemaps_map_v2.2.jpg',
};

const seo = (path?: string, query?: ParsedUrlQuery): BasicSeoType => ({
  '/': {
    title: 'Mapping Network',
    description: 'Welcome to the Mapping Network, a decentralized global map.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/account': {
    title: 'Bee Maps: Account',
    description: 'Access support for your Bee Maps account.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/account/profile': {
    title: 'Bee Maps: My Account',
    description: 'View and manage your Bee Maps account.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/account/support': {
    title: 'Bee Maps: Account Support',
    description: 'Access support for your Bee Maps account.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/console': {
    title: 'Map Data Console',
    description:
      'Manage your Bee Maps data products, generate API keys, purchase map credits and monitor your API usage - all in one central hub.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/console/developers/api-key': {
    title: 'Map Data Console: Developers - API Key',
    description:
      'Manage your Bee Maps data products, generate API keys, purchase map credits and monitor your API usage - all in one central hub.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/console/developers/usage': {
    title: 'Map Data Console: Developers - Usage',
    description:
      'Manage your Bee Maps data products, generate API keys, purchase map credits and monitor your API usage - all in one central hub.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/console/organization/info': {
    title: 'Map Data Console: Organization - Info',
    description:
      'Manage your Bee Maps data products, generate API keys, purchase map credits and monitor your API usage - all in one central hub.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/console/organization/users': {
    title: 'Map Data Console: Organization - Users',
    description:
      'Manage your Bee Maps data products, generate API keys, purchase map credits and monitor your API usage - all in one central hub.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/login': {
    title: 'Bee Maps: Log In',
    description: 'Secure login page for Bee Maps - a decentralized global map.',
    keywords: defaultSeo.keywords,
  },
  '/map': {
    title: 'Bee Maps: Map',
    description: 'Experience the map - Map Feature Detections.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/network/ai-trainers-reviews': {
    title: 'Bee Maps: AI Trainers Reviews',
    description: "Explore the Network's metrics of global AI Trainers reviews.",
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/network/contributors': {
    title: 'Bee Maps: Contributors',
    description: 'Explore metrics of global contributors.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/network/coverage': {
    title: 'Bee Maps: Network Coverage',
    description: 'Explore the global coverage.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/network/regions': {
    title: 'Bee Maps: Network - Regions',
    description: 'Explore the coverage across global regions.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/privacy/overview': {
    title: 'Privacy Overview',
    description: 'Privacy is at the core of the mission.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/privacy/privacy-policy': {
    title: 'Privacy Policy',
    description: 'Privacy is at the core of the mission.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/privacy/redaction-request': {
    title: 'Redaction Request',
    description:
      'Bee Maps can assist with blurring or deletion of images collected by the network.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/product': {
    title: 'Map Data Products',
    description:
      'Bee Maps APIs and tools offer fresh street-level map imagery and data to enhance navigation, automotive, logistics, and government sectors.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/product/bursts': {
    title: 'Bursts: On-Demand, Targeted Map Data',
    description:
      'Bee Maps Bursts provide on-demand, targeted map data collection. Our global mapping network will quickly and cost-effectively collect the data you need.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/product/map-features-api': {
    title: 'Map Features API: Access Geolocated Traffic Infrastructure',
    description:
      'The Map Features API provides precisely positioned map features, including speed signs, turn restrictions, stop signs, highway exits, and traffic lights.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/product/map-image-api': {
    title: 'Map Image API: Access High-Quality Street-Level Imagery',
    description:
      'Hivemapper’s Map Image API connects your business to fresh, high-quality, and standardized street-level imagery collected by Hivemapper’s global mapping network.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/product/pricing': {
    title: 'Pricing: Map Data Products',
    description:
      'Bee Maps APIs and tools offer fresh street-level map imagery and data to enhance navigation, automotive, logistics, and government sectors.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/product/scout': {
    title: 'Scout: Advanced Location Monitoring',
    description:
      'Scout is a powerful location-monitoring tool offering fresh, high-quality imagery and customizable UI options for diverse applications.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/scout': {
    title: 'Scout: Advanced Location Monitoring',
    description:
      'Scout is a powerful location-monitoring tool offering fresh, high-quality imagery and customizable UI options for diverse applications.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/scout/list': {
    title: 'Scout: Advanced Location Monitoring',
    description:
      'Scout is a powerful location-monitoring tool offering fresh, high-quality imagery and customizable UI options for diverse applications.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/scout/location': {
    title: 'Scout: Advanced Location Monitoring',
    description:
      'Scout is a powerful location-monitoring tool offering fresh, high-quality imagery and customizable UI options for diverse applications.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/scout/map': {
    title: 'Scout: Advanced Location Monitoring',
    description:
      'Scout is a powerful location-monitoring tool offering fresh, high-quality imagery and customizable UI options for diverse applications.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/tos/hivemapper-dashcam': {
    title: 'Terms of Service - Dashcam',
    description:
      'Find terms of service and policies for Bee Maps products and services.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/tos/pre-orders': {
    title: 'Terms of Service - Dashcam Pre-Orders',
    description:
      'Find terms of service and policies for Bee Maps products and services.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/tos/hivemapper-dashcam-terms': {
    title: 'Terms of Service - Dashcam',
    description:
      'Find terms of service and policies for Bee Maps products and services.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/tos/map-products': {
    title: 'Terms of Service - Map Products',
    description:
      'Find terms of service and policies for Bee Maps products and services.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/tos/mapping-network': {
    title: 'Terms of Service - Mapping Network',
    description:
      'Find terms of service and policies for Bee Maps products and services.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/trainers': {
    title: 'Train Map AI',
    description:
      "Earn HONEY rewards while training Map AI in helping build the world's freshest map.",
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/use-cases': {
    title: 'Use Cases',
    description:
      'Bee Maps APIs and tools offer fresh street-level map imagery and data to enhance navigation, automotive, logistics, and government sectors.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/use-cases/automotive': {
    title: 'Fresh Map Data for Autonomous Driving & ADAS',
    description:
      'Bee Maps APIs empower automotive firms with accurate and dynamic data for autonomous vehicles, ADAS, and AI/machine learning.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/use-cases/government-city': {
    title: 'Map Solutions for City and Government Agencies',
    description:
      'Bee Maps provides data-driven APIs and map tools to help city and government organizations manage urban planning and infrastructure.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },

  '/use-cases/insurance': {
    title: 'Detailed Map Imagery and Data for Insurance',
    description:
      'Bee Maps provides powerful map data APIs and tools for insurance risk assessments, property valuations, claims investigations, and more.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/use-cases/navigation-logistics': {
    title: 'Map Data Tools for Navigation & Logistics',
    description:
      'Upgrade navigation and logistics services with fresh, accurate map data. Enhance route planning, regulatory compliance, and operational efficiency.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/use-cases/real-estate': {
    title: 'Fresh Map Imagery for Real Estate',
    description:
      'Bee Maps APIs and products provide dynamic insights for real estate developers, investors, brokers, and agents to analyze markets, assess properties, and more.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/fleet-management': {
    title: 'Guild: Split tokens between contributors. ',
    description:
      'A Bee Maps Guild is a tool for splitting tokens between contributors.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/admin/regions': {
    title: 'Regions',
    description: 'View regions in the Bee Maps network.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/admin/regions/*': {
    title: 'Regions Inspector' + (query?.id ? ` - ${query.id}` : ''),
    description: 'Inspect and manage regions in the Bee Maps network.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/admin/objectdetections': {
    title: 'Object Detections',
    description: 'View object detections in the Bee Maps network.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/admin/objectdetections/*': {
    title: 'Object Detections Inspector' + (query?.id ? ` - ${query.id}` : ''),
    description:
      'Inspect and manage object detections in the Bee Maps network.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/admin/dashcamframe': {
    title: 'Dashcam Frame Inspector',
    description: 'View dashcam frames in the Bee Maps network.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/admin/dashcamframe/*': {
    title: 'Dashcam Frame Inspector' + (query?.id ? ` - ${query.id}` : ''),
    description: 'Inspect and manage dashcam frames in the Bee Maps network.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/admin/landmarks': {
    title: 'Landmarks Inspector',
    description: 'View landmarks in the Bee Maps network.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/admin/landmarks/*': {
    title: 'Landmarks Inspector' + (query?.id ? ` - ${query.id}` : ''),
    description: 'Inspect and manage landmarks in the Bee Maps network.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/admin/landmarkpair': {
    title: 'Landmark Pairs Inspector',
    description: 'View landmark pairs in the Bee Maps network.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/admin/landmarkpair/*': {
    title: 'Landmarks Pairs Inspector' + (query?.id ? ` - ${query.id}` : ''),
    description: 'Inspect and manage landmark pairs in the Bee Maps network.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/admin/mapfeaturecandidates': {
    title: 'Map Feature Candidates Inspector',
    description: 'View map feature candidates pairs in the Bee Maps network.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/admin/mapfeaturecandidates/*': {
    title:
      'Map Feature Candidates Inspector' + (query?.id ? ` - ${query.id}` : ''),
    description:
      'Inspect and manage map feature candidates in the Bee Maps network.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/admin/mapfeatures': {
    title: 'Map Features Inspector',
    description: 'View map features in the Bee Maps network.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/admin/mapfeatures/*': {
    title: 'Map Features Inspector' + (query?.id ? ` - ${query.id}` : ''),
    description: 'Inspect and manage map features in the Bee Maps network.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/admin/detectionareas': {
    title: 'Detection Areas Inspector',
    description: 'View detection areas in the Bee Maps network.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/admin/detectionareas/*': {
    title: 'Detection Areas Inspector' + (query?.id ? ` - ${query.id}` : ''),
    description: 'Inspect and manage detection areas in the Bee Maps network.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/admin/constructiondata': {
    title: 'Construction Data Evaluation',
    description: 'Download construction data from the Bee Maps network.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/admin/worker-teams': {
    title: 'Worker Teams',
    description: 'View worker teams in the Bee Maps network.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/admin/trackedCells': {
    title: 'Tracked Cells Inspector',
    description: 'View tracked cells in the Bee Maps network.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/admin/trackedCells/*': {
    title: 'Tracked Cells Inspector' + (query?.id ? ` - ${query.id}` : ''),
    description: 'Inspect and manage tracked cells in the Bee Maps network.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/admin/pipeline/coverage-map*': {
    title: 'Coverage Map',
    description: 'View coverage map in the Bee Maps network.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/admin/pipeline/landmarks*': {
    title: 'Landmark ' + (query?.id ? ` - ${query.id}` : ''),
    description: 'View landmark',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/admin/pipeline/mapfeatures*': {
    title: 'Map Feature' + (query?.id ? ` - ${query.id}` : ''),
    description: 'View map feature',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/admin/pipeline/segments*': {
    title: 'Segment ' + (query?.id ? ` - ${query.id}` : ''),
    description: 'View segment',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/admin/pipeline/objectdetections*': {
    title: 'Object Detection' + (query?.id ? ` - ${query.id}` : 's'),
    description: 'View object detection',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/admin/pipeline/taxonomy*': {
    title: 'Object class' + (query?.id ? ` - ${query.id}` : 'es'),
    description: 'View object class',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
  '/admin/*': {
    title: 'Bee Maps Admin' + (query?.id ? ` - ${query.id}` : ''),
    description: 'Admin page for Bee Maps.',
    keywords: defaultSeo.keywords,
    image: defaultSeo.image,
  },
});

export function wildcardToRegExp(pattern: string): RegExp {
  const escaped = pattern.replace(/[-/\\^$+?.()|[\]{}]/g, '\\$&');
  const withWildcards = escaped.replace(/\*/g, '.*');
  return new RegExp(`^${withWildcards}$`);
}

export function getSeoByPath(asPath: string, query: ParsedUrlQuery): SeoType {
  const compiled = Object.entries(seo(asPath, query)).map(
    ([pattern, meta]) => ({
      regex: wildcardToRegExp(pattern),
      meta,
      weight: pattern.replace('*', '').length,
    }),
  );

  compiled.sort((a, b) => b.weight - a.weight);

  const found = compiled.find(({ regex }) => regex.test(asPath));
  return found ? found.meta : defaultSeo;
}
